/**
 * AuthNav - An organism component for authentication navigation controls.
 *
 * Combines UserMenu and LoginButton with business logic for authentication state.
 * Uses auth hooks to determine which component to render.
 */

import { useNavigate } from '@tanstack/react-router';
import { useTrackerCapabilities } from '@/features/tracker';
import { useAuth, useLogout } from '../../hooks';
import { UserMenu, type SyncStatus } from '../molecules/UserMenu';
import { LoginButton } from '../atoms/LoginButton';

export interface AuthNavProps {
  /**
   * Additional CSS classes.
   */
  className?: string;
  /**
   * Current sync/connection status. Only shown when a simulation is selected.
   */
  syncStatus?: SyncStatus;
  /**
   * Callback when sync status is clicked (to toggle between modes).
   */
  onSyncClick?: () => void;
}

export function AuthNav({ className, syncStatus, onSyncClick }: AuthNavProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { logout } = useLogout();
  const navigate = useNavigate();
  // Tracker nav entries appear only when the deployment is licensed for it.
  // (The query is auth-gated, so it stays idle for logged-out visitors.)
  const { data: trackerCapabilities } = useTrackerCapabilities();

  const handleLogout = () => {
    logout();
    navigate({ to: '/' });
  };

  const handleLoginClick = () => {
    navigate({ to: '/login' });
  };

  const handleManageUsers = () => {
    navigate({ to: '/admin/users' });
  };

  const handleLicense = () => {
    navigate({ to: '/admin/license' });
  };

  const handleTrackerSources = () => {
    navigate({ to: '/admin/tracker-sources' });
  };

  const handleDatasets = () => {
    navigate({ to: '/datasets' });
  };

  const handleAccountSettings = () => {
    navigate({ to: '/account' });
  };

  // While validating stored token, don't show login button or user menu
  if (isLoading) {
    return null;
  }

  if (isAuthenticated && user) {
    return (
      <UserMenu
        user={user}
        onLogout={handleLogout}
        onManageUsersClicked={handleManageUsers}
        onLicenseClicked={handleLicense}
        onTrackerSourcesClicked={handleTrackerSources}
        onDatasetsClicked={handleDatasets}
        showTracker={trackerCapabilities?.licensed ?? false}
        onAccountSettingsClicked={handleAccountSettings}
        syncStatus={syncStatus}
        onSyncClick={onSyncClick}
        className={className}
      />
    );
  }

  return <LoginButton onLoginClicked={handleLoginClick} className={className} />;
}
